import React, { useState, useEffect } from 'react';
import {PlusCircle, Save, X, Shield, Briefcase, UserCheck, Info, Search, Check} from 'lucide-react';
import server from '../../../api/server.js';
import { FaTimes } from 'react-icons/fa';
import UnauthorizedPage from '../../layouts/UnauthorizedPage.jsx';
import ModernTimeDropdown from '../../layouts/modernDropdown.jsx';
import { useToast } from '../../../context/ToastContext.jsx';

const BUSINESS_WEEK_DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const DEFAULT_BUSINESS_DAYS = BUSINESS_WEEK_DAYS.map((day) => day.value);

const ProjectSettingsPage = ({serviceID, clientID}) => {
  const { showToast } = useToast();
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('All')
  // States
  const [projectName, setProjectName] = useState('');
  const [projectContext, setProjectContext] = useState('')
  const [mcpServerContext, setMcpServerContext] = useState('')
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [organizationContext, setOrganizationContext] = useState('')
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [email, setEmail] = useState(null)
  const [client_id, setClient_id] = useState(null)
  const [service_id, setService_id] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [teamMembers, setTeamMembers] = useState([])
  const [useremail, setUseremail] = useState(null)
  const [name, setName] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [toDeleteObj, setToDeleteObj] = useState(null)
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [formloading, setformloading] = useState(false)
  const [employeesList, setEmployeesList] = useState([])
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([])
  const [transcriptionHint, setTranscriptionHint] = useState('')
  const [businessHours, setBusinessHours] = useState({start_time: '', end_time: '', working_days: DEFAULT_BUSINESS_DAYS})
  const [teams, setTeams] = useState([])
  const [teamName, setTeamName] = useState('')
  const [teamEmployees, setTeamEmployees] = useState([])
  const [editingTeamId, setEditingTeamId] = useState(null)
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [teamFormLoading, setTeamFormLoading] = useState(false)
  const [showTeamFormModal, setShowTeamFormModal] = useState(false)
  const [teamToDelete, setTeamToDelete] = useState(null)
  const [role] = useState(() => {
    const userCookie = getCookie('dashboard_login_user_creds');
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      const foundRole = userData.role
      return foundRole;
    }
    return 'admin';
  })

  function getCookie(name) {
      function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
      var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
      return match ? match[1] : null;
  }
  const hitDeleteApi = async () => {
    try {
      setIsLoading(true)
      let res = await server.deleteProjectMembersSettings(toDeleteObj)
      if(res.status === 200){
        getTeamMembersData()
        showToast('User deleted successfully.', "success")
        setToDeleteObj(null)
      }
      setIsLoading(false)
      setShowConfirmBox(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  const deleteMember = async(useremail) => {
      setShowConfirmBox(true)
      let obj = {useremail, email, client_id, service_id, menu: "project-settings", submenu: undefined}
      setToDeleteObj(obj)
  }

  const updateProjectConfig = async() => {
    try {
      let obj = {menu: 'project-settings', submenu: undefined, email, client_id, service_id, 'project_name': projectName,
        'organization_context': organizationContext, 'project_context': projectContext, 'knowledge_base': knowledgeBase, 'transcription_hint': transcriptionHint,
        'mcp_server_context': mcpServerContext,
        'business_hours': {
          start_time: formatBusinessTimeForApi(businessHours.start_time),
          end_time: formatBusinessTimeForApi(businessHours.end_time),
          working_days: normalizeBusinessDaysForApi(businessHours.working_days),
        }
      }
      let res = await server.updateProjectDetails(obj)
      if(res.status === 200){
        showToast('Project Settings Updated Successfully.', "success")
      }
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false);
    }
  }

  const getTeamMembersData = async() => {
    try {
      setIsLoading(true)
      let res = await server.getProjectMembersSettings(client_id, service_id, email, 'project-settings', undefined)
      if(res.status === 200){
        let resp = await res.json()
        setProjectName(resp.data.project_settings?.project_name)
        setProjectContext(resp.data.project_settings?.project_context)
        setMcpServerContext(resp.data.project_settings?.mcp_server_context ?? '')
        setKnowledgeBase(resp.data.project_settings?.knowledge_base)
        setOrganizationContext(resp.data.project_settings?.organization_context)
        setTranscriptionHint(resp.data.project_settings?.transcription_hint)
        setBusinessHours({
          start_time: formatBusinessTimeForInput(resp.data.project_settings?.business_hours?.start_time),
          end_time: formatBusinessTimeForInput(resp.data.project_settings?.business_hours?.end_time),
          working_days: normalizeBusinessDaysForInput(resp.data.project_settings?.business_hours?.working_days ?? resp.data.project_settings?.business_hours?.days),
        })
        const formattedMembers = [
          ...resp.data.admins.map(admin => ({
            id: admin.email,
            name: admin.name,
            email: admin.email,
            role: 'Admin',
            canEdit: false,
            editReason: "Cannot modify"
          })),
          ...resp.data.users.map(user => ({
            id: user.email,
            name: user.name,
            email: user.email,
            role: user.role,
            canEdit: true,
            editReason: null
          }))
        ];
        setTeamMembers(formattedMembers);
        setFilteredTeamMembers(formattedMembers)
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
    }
  }
  const handleAddMember = async () => {
    try {
      setformloading(true)
      let obj = {useremail, email, name, role, client_id, service_id, 'menu': 'project-settings', 'submenu': undefined}
      let res;
      if (isEdit){
        res = await server.editProjectMemberSettings(obj);
      }else{
        res = await server.postProjectMembersSettings(obj);
      }
      let resp = await res.json()
      if(res.status === 200){
        showToast(isEdit ? 'User Updated Successfully!' : 'User Added Successfully!', "success")
        getTeamMembersData()
        setformloading(false)
        setIsEdit(false)
        setName('')
        setUseremail('')
        setShowAddMemberForm(false);
      }else if(resp.error){
        showToast(resp.error, "error")
      }
      setformloading(false)
    } catch (error) {
      setformloading(false)
    }
  }

  useEffect(() => {
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user) {
      setEmail(user.email);
    }
    if (serviceID) {
      setService_id(serviceID);
    }
    if(clientID){
      setClient_id(clientID)
    }
  }, [serviceID, clientID]);

  const getEmployeesList = async () => {
    try {
      let res = await server.getAdvancedFilterDetailsAgentFeedback(client_id, service_id, email, 'project-settings', undefined)
      if(res.status === 200){
        let resp = await res.json()
        setEmployeesList(resp.employee_id)
      }
    } catch (error) {
    }
  }

  const normalizeTeamsResponse = (data = {}) => {
    const source = data.teams || data.data || data.team_employee_mapping || data.dashboard_team_employee_mapping || [];
    return Array.isArray(source) ? source : [];
  }

  const resetTeamForm = () => {
    setTeamName('');
    setTeamEmployees([]);
    setEditingTeamId(null);
  }

  const getDashboardTeams = async () => {
    try {
      setTeamsLoading(true);
      const res = await server.getDashboardTeams(client_id, service_id, email, 'project-settings', undefined);
      if (res.status === 200) {
        const resp = await res.json();
        setTeams(normalizeTeamsResponse(resp));
      }
      setTeamsLoading(false);
    } catch (error) {
      setTeamsLoading(false);
    }
  }

  const handleSaveTeam = async () => {
    const normalizedTeamName = String(teamName || '').trim();
    if (!normalizedTeamName) {
      showToast('Team name is required.', 'error');
      return;
    }
    try {
      setTeamFormLoading(true);
      const obj = {
        action: editingTeamId ? 'update' : 'create',
        team_id: editingTeamId || `team_${Date.now()}`,
        team_name: normalizedTeamName,
        employees: Array.isArray(teamEmployees) ? teamEmployees : [],
        email,
        client_id,
        service_id,
        menu: 'project-settings',
        submenu: undefined,
      };
      const res = await server.postDashboardTeam(obj);
      const resp = await res.json().catch(() => ({}));
      if (res.status === 200) {
        showToast(editingTeamId ? 'Team updated successfully.' : 'Team created successfully.', 'success');
        resetTeamForm();
        setShowTeamFormModal(false);
        getDashboardTeams();
      } else if (resp.error) {
        showToast(resp.error, 'error');
      }
      setTeamFormLoading(false);
    } catch (error) {
      setTeamFormLoading(false);
    }
  }

  const handleEditTeam = (team) => {
    setEditingTeamId(team.team_id);
    setTeamName(team.team_name || '');
    setTeamEmployees(Array.isArray(team.employees) ? team.employees : []);
    setShowTeamFormModal(true);
  }

  const handleDeleteTeam = (team) => {
    setTeamToDelete(team);
  }

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    try {
      setTeamsLoading(true);
      const res = await server.postDashboardTeam({
        action: 'delete',
        team_id: teamToDelete.team_id,
        team_name: teamToDelete.team_name,
        email,
        client_id,
        service_id,
        menu: 'project-settings',
        submenu: undefined,
      });
      const resp = await res.json().catch(() => ({}));
      if (res.status === 200) {
        showToast('Team deleted successfully.', 'success');
        if (editingTeamId === teamToDelete.team_id) resetTeamForm();
        setTeamToDelete(null);
        getDashboardTeams();
      } else if (resp.error) {
        showToast(resp.error, 'error');
      }
      setTeamsLoading(false);
    } catch (error) {
      setTeamsLoading(false);
    }
  }

  const handleSearch = (value) => {
    setSearch(value)
    applyFilters(value, roleFilter)
  }
  
  const handleRoleFilter = (value) => {
    setRoleFilter(value)
    applyFilters(search, value)
  }

  const formatBusinessTimeForInput = (value) => {
    if (!value || typeof value !== 'string') return '';
    const [hours = '00', minutes = '00'] = value.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }

  const formatBusinessTimeForApi = (value) => {
    if (!value || typeof value !== 'string') return '';
    const [hours = '00', minutes = '00', seconds = '00'] = value.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
  }

  const normalizeBusinessDay = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return DEFAULT_BUSINESS_DAYS.includes(normalized) ? normalized : '';
  }

  const normalizeBusinessDaysForInput = (value) => {
    if (!Array.isArray(value)) return DEFAULT_BUSINESS_DAYS;
    const normalizedDays = value.map(normalizeBusinessDay).filter(Boolean);
    return Array.from(new Set(normalizedDays));
  }

  const normalizeBusinessDaysForApi = (value) => {
    if (!Array.isArray(value)) return DEFAULT_BUSINESS_DAYS;
    const normalizedDays = value.map(normalizeBusinessDay).filter(Boolean);
    return Array.from(new Set(normalizedDays));
  }

  const getSelectedBusinessDays = () => (
    Array.isArray(businessHours.working_days) ? businessHours.working_days : DEFAULT_BUSINESS_DAYS
  );

  const toggleBusinessDay = (dayValue) => {
    setBusinessHours((prev) => {
      const currentDays = Array.isArray(prev.working_days) ? prev.working_days : DEFAULT_BUSINESS_DAYS;
      const nextDays = currentDays.includes(dayValue)
        ? currentDays.filter((day) => day !== dayValue)
        : [...currentDays, dayValue];
      return {...prev, working_days: nextDays};
    });
  }
  
  const applyFilters = (searchValue, roleValue) => {
    let filtered = teamMembers;
    
    // Apply search filter
    if (searchValue && searchValue.trim() !== '') {
      filtered = filtered.filter((member) => 
        member.name.toLowerCase().includes(searchValue.toLowerCase()) || 
        member.email.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Apply role filter
    if (roleValue && roleValue !== 'All') {
      filtered = filtered.filter((member) => member.role.toLowerCase() === roleValue.toLowerCase());
    }
    
    setFilteredTeamMembers(filtered);
  }

  useEffect(() => {
    if (client_id && service_id && email) {
      getTeamMembersData();
      getEmployeesList();
      getDashboardTeams();
    }
  }, [client_id, service_id, email]);

  // Reapply filters when teamMembers changes
  useEffect(() => {
    applyFilters(search, roleFilter);
  }, [teamMembers]);
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowAddMemberForm(false);
        setShowTeamFormModal(false);
        setTeamToDelete(null);
        resetTeamForm();
      }
    }
    if (showAddMemberForm || showTeamFormModal || teamToDelete) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    }
  }, [showAddMemberForm, showTeamFormModal, teamToDelete])

  return (
    <div className="flex flex-col bg-white font-sans">
      
      {/* Main Content */}
      {role !== 'employee' && <div className="flex flex-1">
        
        {/* Content */}
        <main className="flex-1 py-3 px-5 bg-gray-50">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Project Settings</h1>
            <p className="text-sm text-gray-500">Manage your project details and team members</p>
          </div>
          
          {/* Project Details Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Project Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Project Name */}
              <div className='md:col-span-2'>
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Project Name</label>
                  <div className="ml-1 relative">
                    <div className="group">
                      <Info className="h-4 w-4 text-gray-500 cursor-help" />
                      <div className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 shadow-sm text-gray-700 text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                        Project name can be changed by an admin in Organization Settings. The project ID remains permanent.
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  type="text" 
                  value={projectName}
                  disabled
                  className="bg-gray-100 pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>
              {/* Organization Context */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Organization Context</label>
                </div>
                <textarea class="global-input"value={organizationContext} onChange={(e)=>setOrganizationContext(e.target.value)}
                  rows="3"
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>
              
              {/* Project Context */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Project Context</label>
                </div>
                <textarea class="global-input"value={projectContext} onChange={(e)=>setProjectContext(e.target.value)}
                  rows="3"
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>

              {/* Key Products & Features */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Knowledge Base</label>
                </div>
                <textarea class="global-input"value={knowledgeBase} onChange={(e)=>setKnowledgeBase(e.target.value)}
                  rows="7"
                  placeholder="Enter project's products and features, one per line"
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>

              {/* Transcription Hints */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Transcription Hint</label>
                </div>
                <textarea class="global-input"value={transcriptionHint} onChange={(e)=>setTranscriptionHint(e.target.value)}
                  rows="5" maxLength={3000}
                  placeholder="Enter transcription hint"
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>

              {/* MCP Server Context */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">MCP Server Context</label>
                </div>
                <textarea
                  value={mcpServerContext}
                  onChange={(e) => setMcpServerContext(e.target.value)}
                  rows="7"
                  placeholder="Enter project-specific context for connected MCP clients"
                  className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                />
              </div>

              {/* Business Hours */}
              <div className="md:col-span-2">
                <div className="flex items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Business Hours</label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
                    <input
                      type="time"
                      step="1"
                      value={businessHours.start_time}
                      onChange={(e) => setBusinessHours(prev => ({...prev, start_time: e.target.value}))}
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End Time</label>
                    <input
                      type="time"
                      step="1"
                      value={businessHours.end_time}
                      onChange={(e) => setBusinessHours(prev => ({...prev, end_time: e.target.value}))}
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-medium text-gray-500 mb-2">Working Days</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {BUSINESS_WEEK_DAYS.map((day) => (
                      <label key={day.value} className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 cursor-pointer hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={getSelectedBusinessDays().includes(day.value)}
                          onChange={() => toggleBusinessDay(day.value)}
                          className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button onClick={updateProjectConfig} className="flex items-center bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700">
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </button>
            </div>
          </div>
          
          {/* Team Management Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Team Management</h2>
              <button
                onClick={() => {
                  resetTeamForm();
                  setShowTeamFormModal(true);
                }}
                className="flex items-center bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Create Team
              </button>
            </div>

            <div className="overflow-hidden border border-gray-200 rounded-lg">
              {teamsLoading && (
                <div className="flex justify-center items-center py-10" style={{ flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )}
              {!teamsLoading && (
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-9">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employees</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {teams.length === 0 && (
                        <tr>
                          <td className="px-6 py-4 text-sm text-gray-500" colSpan="3">No teams created yet.</td>
                        </tr>
                      )}
                      {teams.map((team) => {
                        const employees = Array.isArray(team.employees) ? team.employees : [];
                        return (
                          <tr key={team.team_id || team.team_name} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{team.team_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {employees.length > 0 ? employees.join(', ') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button onClick={() => handleEditTeam(team)} className="hover:underline text-indigo-600 mr-2">
                                Edit
                              </button>
                              <button onClick={() => handleDeleteTeam(team)} className="hover:underline text-red-600">
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Create/Edit Team Modal */}
          {showTeamFormModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">{editingTeamId ? 'Edit' : 'Create'} Team</h3>
                  <button
                    onClick={() => {
                      setShowTeamFormModal(false);
                      resetTeamForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>

                {!teamFormLoading && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                      <input
                        type="text"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="Enter team name"
                        className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Employees</label>
                      <ModernTimeDropdown
                        width="w-full"
                        placeholder="Select Employees"
                        value={teamEmployees}
                        onChange={(e) => setTeamEmployees(e.target.value)}
                        options={employeesList}
                        isMulti
                        showBulkActions
                      />
                    </div>
                  </div>
                )}
                {teamFormLoading && (
                  <div className="flex justify-center items-center py-10" style={{ flexDirection: "column" }}>
                    <div className="loader"></div>
                    <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                  </div>
                )}

                <div className="flex justify-end p-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowTeamFormModal(false);
                      resetTeamForm();
                    }}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTeam}
                    disabled={teamFormLoading}
                    className={`bg-indigo-600 text-white px-4 py-2 rounded text-sm ${teamFormLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                  >
                    {editingTeamId ? 'Update' : 'Create'} Team
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Team Confirmation Modal */}
          {teamToDelete && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Delete team?</h2>
                  <button
                    onClick={() => setTeamToDelete(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>

                <div className="p-6 pt-2">
                  <p className="text-gray-500 text-sm">
                    Are you sure you want to delete {teamToDelete.team_name ? `"${teamToDelete.team_name}"` : 'this team'}?
                  </p>
                </div>

                <div className="flex justify-end gap-3 p-6 pt-0">
                  <button
                    onClick={() => setTeamToDelete(null)}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    style={{height: "40px", borderRadius: "4px"}}
                  >
                    No
                  </button>
                  <button
                    onClick={confirmDeleteTeam}
                    disabled={teamsLoading}
                    className={`px-6 py-2 rounded-lg bg-indigo-600 text-white ${teamsLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'}`}
                    style={{height: "40px", borderRadius: "4px"}}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Role Management Section */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Role Management</h2>
              <button 
                onClick={() => setShowAddMemberForm(true)}
                className="flex items-center bg-indigo-600 text-white px-3 py-2 rounded text-sm hover:bg-indigo-700"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Team Member
              </button>
            </div>
            
            {/* Role Description Cards */}
            <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-200">
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <Shield className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Admin</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Access to everything on the dashboard</li>
                    <li>Can add admins, project owners & team members</li>
                    <li>Access to organization settings</li>
                  </ul>
                </div>
                
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <Briefcase className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Manager</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Project level access</li>
                    <li>Can add team members</li>
                    <li>Can have access to multiple projects</li>
                    <li>No access to organization settings</li>
                  </ul>
                </div>
                
                <div className="flex-1 p-4 bg-white">
                  <div className="flex items-center mb-2">
                    <UserCheck className="h-5 w-5 text-indigo-500 mr-2" />
                    <h3 className="font-medium text-gray-900">Employee</h3>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
                    <li>Limited access</li>
                    <li>Access to their own data</li>
                    <li>No management capabilities</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Members Search and Filter */}
            <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-4 justify-between">
                <div className="relative w-2/3">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input value={search} onChange={(e)=>handleSearch(e.target.value)}
                    type="text"
                    placeholder="Search by name or email"
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                  />
                </div>
                
                <div className="w-1/3">
                <ModernTimeDropdown value={roleFilter} onChange={(e)=>handleRoleFilter(e.target.value)} options={['All', 'Admin', 'Manager', 'Employee']} width="w-full"/>
                </div>
              </div>
            </div>
            
            {/* Members Table */}
            <div className="overflow-hidden border border-gray-200 rounded-lg">
            {
              isLoading && (
                <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )
            }
              {!isLoading && (
                <div className="max-h-80 overflow-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0 z-9">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTeamMembers.map((member) => (
                        <tr key={member.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center text-gray-700 mr-3">
                                {member.name.charAt(0)}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{member.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {member.role === 'Admin' && <Shield className="h-4 w-4 text-indigo-500 mr-2" />}
                              {member.role === 'manager' && <Briefcase className="h-4 w-4 text-indigo-500 mr-2" />}
                              {member.role === 'employee' && <UserCheck className="h-4 w-4 text-indigo-500 mr-2" />}
                              <span className="text-sm text-gray-900 capitalize">{member.role}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {role !== 'Employee' && email !== member.email && member.role === 'employee' ? (
                              <div>
                                <button
                                    onClick={() => {
                                      setShowAddMemberForm(true);
                                      setName(member.name);
                                      setUseremail(member.email);
                                      setIsEdit(true);
                                    }}
                                    className="hover:underline text-indigo-600 mr-2"
                                  >
                                    Edit
                                  </button>
                                <button onClick={()=>deleteMember(member.email)} className="hover:underline text-red-600">Remove</button>
                              </div>
                            ) : (
                              <div className="flex items-center text-gray-400">
                                <Info className="h-3 w-3 mr-1" />
                                <span>Modify from Org Settings</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          {/* Add Team Member Form (Modal) */}
          {showAddMemberForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">{isEdit ? 'Edit' : 'Add'} Team Member</h3>
                  <button 
                    onClick={() => {setShowAddMemberForm(false); setName(''); setUseremail('');}}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                {!formloading && <div className="p-4 space-y-4">
                  <div className="bg-indigo-50 border border-indigo-100 rounded p-3 mb-4">
                    <div className="flex items-center text-indigo-800 text-sm">
                      <Info className="h-4 w-6 mr-2 text-indigo-500" />
                      <span>Only employees can be added directly to a project. To add Admins or Managers, go to Organization Settings.</span>
                    </div>
                  </div>
                
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    {/* <input class="global-input"
                      type="text" 
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                      placeholder="Enter full name" value={name} onChange={(e)=>setName(e.target.value)}
                    /> */}
                    <ModernTimeDropdown width="w-full" placeholder="Select Employee" value={name} onChange={(e)=>setName(e.target.value)} options={employeesList}/>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email ID</label>
                    <input class="global-input"
                      type="email" 
                      className="pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                      placeholder="Enter email address" value={useremail} onChange={(e)=> setUseremail(e.target.value.toLowerCase())} disabled={isEdit}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input class="global-input"
                      type="text" 
                      value="Employee" 
                      disabled
                      className="bg-gray-100 pl-3 pr-4 py-2 border border-gray-300 rounded-md w-full text-sm global-input"
                    />
                    {/* <p className="mt-1 text-xs text-indigo-600 flex items-center">
                      <Info className="h-3 w-3 mr-1" />
                      Employee will have access to their own data only. So, keep employee name same as shown in employee filters.
                    </p> */}
                  </div>
                </div>}
                {
                  formloading && (<div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                    <div className="loader"></div>
                    <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                  </div>)

                }
                
                <div className="flex justify-end p-4 border-t border-gray-200">
                  <button
                    onClick={() => {setShowAddMemberForm(false); setName(''); setUseremail(''); setIsEdit(false)}}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded text-sm mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleAddMember()
                      
                    }}
                    className={`bg-indigo-600 text-white px-4 py-2 rounded text-sm ${formloading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isEdit ? 'Update' : 'Add'} Team Member
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Message */}
          {showSuccessMessage && (
            <div className="fixed bottom-4 right-4 bg-green-50 border border-green-300 text-green-800 rounded-md px-4 py-3 shadow-md z-50 flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <p className="font-medium">Success!</p>
                <p className="text-sm">Employee has been added to the project</p>
              </div>
              <button 
                onClick={() => setShowSuccessMessage(false)}
                className="ml-4 text-green-500"
              >
                <FaTimes style={{color: "slategray"}}/>
              </button>
            </div>
          )}
          {
            showConfirmBox && (
              <div style={{zIndex: "9999999999", position: "fixed", top: "0", left: "0", height: "100%", width: "100%", background: "rgba(0,0,0, 0.5)"}}>
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
                <div className="flex justify-between items-center p-4 pb-2">
                  <h2 className="text-xl text-gray-700" style={{fontWeight: "550"}}>Are you sure?</h2>
                  <button 
                    onClick={() => {
                      {setShowConfirmBox(false); setToDeleteObj(null);}
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                  <FaTimes style={{color: "slategray"}}/>
                  </button>
                </div>
                
                <div className="p-6 pt-2">
                  <p className="text-gray-500 text-sm">Team member will lose access to the project.</p>
                </div>
                
                <div className="flex justify-end gap-3 p-6 pt-0">
                  <button
                    onClick={() => {setShowConfirmBox(false); setToDeleteObj(null);}}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: "40px", borderRadius: "4px"}}
                  >
                    No
                  </button>
                  <button onClick={() => hitDeleteApi()}
                    className="px-6 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" style={{height: "40px", borderRadius: "4px"}}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
        </div>
            )
          }
        </main>
      </div>}
      { (role === 'employee') && (
      <UnauthorizedPage/>)
      }
    </div>
  );
};

export default ProjectSettingsPage;
